// Pure helpers behind the approval-flow admin page (kept apart so they can be tested).

export const STEP_TYPE_USER = "USER";
export const STEP_TYPE_DEPARTMENT_CHIEF = "DEPARTMENT_CHIEF";

export const PRINT_BANDS = [
  { value: "BOTTOM", label: "Jos (cu Întocmit)" },
  { value: "TOP", label: "Sus (zona Aprobat)" },
  { value: "HIDDEN", label: "Nu se tipărește" },
];

// What a new info step asks for; the admin adds or renames lines (a "Sumă" line starts with the
// paper's total with VAT).
export const DEFAULT_INFO_LABELS = "Cod angajament\nSumă";
export const MAX_INFO_LABELS = 5;
export const MAX_INFO_LABEL_LENGTH = 64;

export const SERVER_ERRORS = {
  ERROR_INFO_LABELS_INVALID: "Cel mult 5 rânduri diferite, fiecare de cel mult 64 de caractere.",
  ERROR_FLOW_STEP_ORDER_TAKEN: "Există deja un pas cu acest număr de ordine.",
  ERROR_FLOW_STEP_USER_TAKEN:
    "Persoana aprobă deja alt pas. Ca înlocuitor poate apărea în mai mulți pași, dar ca aprobator principal într-unul singur.",
  ERROR_FLOW_CHIEF_STEP_EXISTS: "Fluxul are deja un pas Șef compartiment.",
  ERROR_MISSING_DATA:
    "Date lipsă sau invalide (persoanele trebuie să fie din instituție și să nu fie administratori).",
};

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const idsOf = (people) => (people || []).map((person) => person.id);

export const isChiefStep = (step) =>
  !!step && step.stepType === STEP_TYPE_DEPARTMENT_CHIEF;

/** A person step that fills lines (e.g. the commitment code and sum) instead of approving. */
export const isInfoStep = (step) => !!step && !isChiefStep(step) && (step.infoLabels || []).length > 0;

/** The labels typed in the form, one per line, trimmed, blank lines dropped. */
export const infoLabelsOf = (text) =>
  String(text || "")
    .split("\n")
    .map((label) => label.replace(/\s+/g, " ").trim())
    .filter((label) => label !== "");

/** Field errors for the step form; empty object when it can be saved. */
export const validateFlowStep = (form) => {
  const error = {};
  if (isBlank(form.orderNumber)) {
    error.orderNumber = "Lipseste numarul de ordine";
  } else if (Number(form.orderNumber) < 0 || !Number.isInteger(Number(form.orderNumber))) {
    error.orderNumber = "Numarul de ordine trebuie sa fie un numar intreg, 0 sau mai mare";
  }
  if (form.stepType !== STEP_TYPE_DEPARTMENT_CHIEF) {
    if (isBlank(form.selectedUserId)) {
      error.signedById = "Nu ati selectat persoana care semneaza";
    } else if (
      (form.fallbackIds || []).some((id) => String(id) === String(form.selectedUserId))
    ) {
      error.fallbacks = "Persoana care aprobă nu poate fi și propriul înlocuitor";
    }
  }
  if (isBlank(form.calling)) {
    error.calling = "Lipseste denumirea titlului";
  }
  if (form.stepType !== STEP_TYPE_DEPARTMENT_CHIEF && form.infoStep) {
    const labels = infoLabelsOf(form.infoLabelsText);
    if (labels.length === 0) {
      error.infoLabels = "Scrieți cel puțin un rând de completat";
    } else if (labels.length > MAX_INFO_LABELS || labels.some((label) => label.length > MAX_INFO_LABEL_LENGTH)) {
      error.infoLabels = "Cel mult " + MAX_INFO_LABELS + " rânduri, fiecare de cel mult " + MAX_INFO_LABEL_LENGTH + " de caractere";
    } else if (new Set(labels.map((label) => label.toLowerCase())).size < labels.length) {
      error.infoLabels = "Două rânduri au același nume";
    }
  }
  return error;
};

/**
 * The form after choosing a signer: they leave the fallback list, where the picker could no
 * longer show them (a chosen value missing from the options is hidden).
 */
export const withSigner = (form, userId) => ({
  ...form,
  selectedUserId: userId,
  fallbackIds: (form.fallbackIds || []).filter((id) => String(id) !== String(userId)),
});

/** Request body for POST /flow/add/step. */
export const flowStepPayload = (form, flowId, flowStepId) => {
  const chief = form.stepType === STEP_TYPE_DEPARTMENT_CHIEF;
  // An info step only fills its lines: it edits nothing and registers nothing.
  const info = !chief && !!form.infoStep;
  return {
    flowId: flowId,
    flowStepId: flowStepId,
    orderNumber: Number(form.orderNumber),
    stepType: chief ? STEP_TYPE_DEPARTMENT_CHIEF : STEP_TYPE_USER,
    signedById: chief ? null : form.selectedUserId,
    fallbackIds: chief ? [] : (form.fallbackIds || []).map(Number),
    canEditPapers: !info && !!form.canEditPapers,
    canSetArticolBugetar: !info && !!form.canSetArticolBugetar,
    canSetRegistrationNumber: !chief && !info && !!form.canSetRegistrationNumber,
    infoLabels: info ? infoLabelsOf(form.infoLabelsText) : [],
    printBand: form.printBand || "BOTTOM",
    calling: String(form.calling).trim(),
  };
};

/** Initial form values for a new step or for editing an existing one. */
export const formFromStep = (step) => {
  if (!step) {
    return {
      stepType: STEP_TYPE_USER,
      orderNumber: "",
      selectedUserId: null,
      fallbackIds: [],
      calling: "",
      canEditPapers: false,
      canSetArticolBugetar: false,
      canSetRegistrationNumber: false,
      infoStep: false,
      infoLabelsText: "",
      printBand: "BOTTOM",
    };
  }
  return {
    stepType: step.stepType || STEP_TYPE_USER,
    orderNumber: step.stepOrder,
    selectedUserId: step.signedBy ? step.signedBy.id : null,
    fallbackIds: idsOf(step.fallbacks),
    calling: step.calling || "",
    canEditPapers: !!step.canEditPapers,
    canSetArticolBugetar: !!step.canSetArticolBugetar,
    canSetRegistrationNumber: !!step.canSetRegistrationNumber,
    infoStep: isInfoStep(step),
    infoLabelsText: (step.infoLabels || []).join("\n"),
    printBand: step.printBand || "BOTTOM",
  };
};

/** Step kind as the steps table shows it. */
export const describeStepKind = (step) => {
  if (isChiefStep(step)) {
    return "Șef compartiment";
  }
  return isInfoStep(step) ? "Persoană – completează informații" : "Persoană";
};

/** Who approves the step, as shown in the steps table. */
export const describeApprover = (step) => {
  if (isChiefStep(step)) {
    return "Șeful departamentului care a întocmit referatul (se sare dacă nu are șef)";
  }
  const signer = step.signedBy ? step.signedBy.email : "";
  const fallbacks = (step.fallbacks || []).map((person) => person.email);
  return fallbacks.length > 0
    ? signer + " (înlocuitori: " + fallbacks.join(", ") + ")"
    : signer;
};

/**
 * Options of the fallbacks picker: active colleagues, plus inactive ones already on the
 * list (so they can be seen and removed). The signer is left out.
 */
export const fallbackOptions = (users, form) =>
  (users || [])
    .filter(
      (user) =>
        String(user.id) !== String(form.selectedUserId) &&
        (user.isActive !== false || (form.fallbackIds || []).includes(user.id))
    )
    .map((user) => ({
      value: user.id,
      label: user.isActive === false ? user.email + " (inactiv)" : user.email,
    }));

export const printBandLabel = (value) => {
  const band = PRINT_BANDS.find((b) => b.value === (value || "BOTTOM"));
  return band ? band.label : value;
};
